import { z } from "zod";

const ListingSchema = z.object({
  id: z.string().uuid(),
  sellerId: z.string().uuid(),
  sellerName: z.string(),
  horseName: z.string(),
  breed: z.enum(["THOROUGHBRED", "PUREBRED_ARABIAN", "OTHER"]),
  breedOther: z.string().nullable(),
  dateOfBirth: z.string(),
  ageYears: z.number(),
  sex: z.enum(["COLT", "FILLY", "GELDING", "MARE", "STALLION"]),
  sire: z.string().nullable(),
  dam: z.string().nullable(),
  pricingType: z.enum(["EXACT", "RANGE", "CONTACT"]),
  priceMinor: z.string().nullable(),
  priceMaxMinor: z.string().nullable(),
  currency: z.string(),
  countryCode: z.string(),
  city: z.string(),
  horseProfileUrl: z.string().nullable(),
  description: z.string().nullable(),
  vetDisclosureNotes: z.string().nullable(),
  verifiedOwner: z.boolean(),
  status: z.literal("APPROVED"),
  bookmarked: z.boolean(),
  photos: z.array(
    z.object({
      id: z.string().uuid(),
      url: z.string(),
    }),
  ),
  videos: z.array(
    z.object({
      id: z.string().uuid(),
      filename: z.string(),
      url: z.string(),
    }),
  ),
  vetDocuments: z.array(
    z.object({
      id: z.string().uuid(),
      filename: z.string(),
      url: z.string(),
    }),
  ),
  veterinaryReportSupplied: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  expiresAt: z.string(),
});

const HorseScanFieldSchema = z.object({
  value: z.string().nullable(),
  confidence: z.number().nullable(),
});

export const HorseScanResponseSchema = z.object({
  extractionId: z.string().uuid(),
  kind: z.enum(["HORSE_PASSPORT", "HORSE_NAME_TAG"]),
  fields: z.object({
    horseName: HorseScanFieldSchema,
    breed: HorseScanFieldSchema,
    breedOther: HorseScanFieldSchema,
    dateOfBirth: HorseScanFieldSchema,
    sex: HorseScanFieldSchema,
    sire: HorseScanFieldSchema,
    dam: HorseScanFieldSchema,
  }),
  requiresReview: z.literal(true),
});

export const BrowseResponseSchema = z.object({
  listings: z.array(ListingSchema),
  nextCursor: z.string().max(300).nullable(),
});

export const MarketplaceLocationsSchema = z.object({
  countries: z.array(z.string().length(2)).min(1),
  countryCode: z.string().length(2),
  cities: z.array(z.string().min(1)),
});

export const OwnedListingSchema = ListingSchema.extend({
  status: z.enum([
    "PENDING",
    "APPROVED",
    "REJECTED",
    "SOLD",
    "WITHDRAWN",
    "EXPIRED",
    "REMOVED",
  ]),
});

export const MyMarketplaceSchema = z.object({
  listings: z.array(OwnedListingSchema),
});

export const MessageSchema = z.object({
  id: z.string().uuid(),
  senderId: z.string().uuid(),
  body: z.string(),
  createdAt: z.string(),
});

export const ConversationSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  listingHorseName: z.string(),
  buyerId: z.string().uuid(),
  sellerId: z.string().uuid(),
  messages: z.array(MessageSchema),
  nextCursor: z.string().max(300).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const ConversationSummarySchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  listingHorseName: z.string(),
  buyerId: z.string().uuid(),
  sellerId: z.string().uuid(),
  lastMessage: MessageSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ConversationListSchema = z.object({
  conversations: z.array(ConversationSummarySchema),
  nextCursor: z.string().max(300).nullable(),
});

export const SortSchema = z.enum(["NEWEST", "PRICE_ASC", "PRICE_DESC"]);
export const BreedFilterSchema = z.enum([
  "",
  "THOROUGHBRED",
  "PUREBRED_ARABIAN",
  "OTHER",
]);
export const SexFilterSchema = z.enum([
  "",
  "COLT",
  "FILLY",
  "GELDING",
  "MARE",
  "STALLION",
]);

export type MyMarketplace = z.infer<typeof MyMarketplaceSchema>;
export type MarketplaceConversation = z.infer<typeof ConversationSchema>;
export type OwnedListing = z.infer<typeof OwnedListingSchema>;
export type Filters = {
  readonly search: string;
  readonly breed: z.infer<typeof BreedFilterSchema>;
  readonly sex: z.infer<typeof SexFilterSchema>;
  readonly minimumAge: string;
  readonly maximumAge: string;
  readonly minimumPrice: string;
  readonly maximumPrice: string;
  readonly currency: "AED";
  readonly location: string;
  readonly verifiedOnly: boolean;
  readonly sort: z.infer<typeof SortSchema>;
  readonly cursor: string;
};

export const EMPTY_FILTERS: Filters = {
  search: "",
  breed: "",
  sex: "",
  minimumAge: "",
  maximumAge: "",
  minimumPrice: "",
  maximumPrice: "",
  currency: "AED",
  location: "",
  verifiedOnly: false,
  sort: "NEWEST",
  cursor: "",
};

export type MarketplaceFilterError =
  "AGE_RANGE" | "INVALID_NUMBER" | "PRICE_RANGE";

const AGE_PATTERN = /^(?:[0-9]|[1-3][0-9]|40)$/u;
const PRICE_PATTERN = /^(0|[1-9]\d{0,12})(?:\.(\d{1,2}))?$/u;

export function marketplaceFiltersFromSearchParams(
  searchParams: Pick<URLSearchParams, "get">,
): Filters {
  const minimumAge = validParam(searchParams.get("minimumAge"), AGE_PATTERN);
  let maximumAge = validParam(searchParams.get("maximumAge"), AGE_PATTERN);
  const minimumPrice = validParam(
    searchParams.get("minimumPrice"),
    PRICE_PATTERN,
  );
  let maximumPrice = validParam(
    searchParams.get("maximumPrice"),
    PRICE_PATTERN,
  );
  if (
    minimumAge !== "" &&
    maximumAge !== "" &&
    Number(minimumAge) > Number(maximumAge)
  ) {
    maximumAge = "";
  }
  if (
    minimumPrice !== "" &&
    maximumPrice !== "" &&
    BigInt(marketplacePriceToMinor(minimumPrice)) >
      BigInt(marketplacePriceToMinor(maximumPrice))
  ) {
    maximumPrice = "";
  }

  return {
    search: boundedText(searchParams.get("search"), 120),
    breed: BreedFilterSchema.catch("").parse(searchParams.get("breed") ?? ""),
    sex: SexFilterSchema.catch("").parse(searchParams.get("sex") ?? ""),
    minimumAge,
    maximumAge,
    minimumPrice,
    maximumPrice,
    currency: "AED",
    location: boundedText(searchParams.get("location"), 120),
    verifiedOnly: searchParams.get("verifiedOwner") === "true",
    sort: SortSchema.catch("NEWEST").parse(
      searchParams.get("sort") ?? "NEWEST",
    ),
    cursor: boundedText(searchParams.get("cursor"), 300),
  };
}

export function marketplaceFiltersSearchParams(
  filters: Filters,
): URLSearchParams {
  const params = new URLSearchParams();
  setNonEmpty(params, "search", filters.search.trim());
  setNonEmpty(params, "breed", filters.breed);
  setNonEmpty(params, "sex", filters.sex);
  setNonEmpty(params, "minimumAge", filters.minimumAge);
  setNonEmpty(params, "maximumAge", filters.maximumAge);
  setNonEmpty(params, "minimumPrice", filters.minimumPrice);
  setNonEmpty(params, "maximumPrice", filters.maximumPrice);
  if (filters.minimumPrice !== "" || filters.maximumPrice !== "") {
    params.set("currency", filters.currency);
  }
  setNonEmpty(params, "location", filters.location.trim());
  if (filters.verifiedOnly) params.set("verifiedOwner", "true");
  if (filters.sort !== "NEWEST") params.set("sort", filters.sort);
  setNonEmpty(params, "cursor", filters.cursor);
  return params;
}

export function marketplaceFilterError(
  filters: Filters,
): MarketplaceFilterError | null {
  if (
    !validOptional(filters.minimumAge, AGE_PATTERN) ||
    !validOptional(filters.maximumAge, AGE_PATTERN) ||
    !validOptional(filters.minimumPrice, PRICE_PATTERN) ||
    !validOptional(filters.maximumPrice, PRICE_PATTERN)
  ) {
    return "INVALID_NUMBER";
  }
  if (
    filters.minimumAge !== "" &&
    filters.maximumAge !== "" &&
    Number(filters.minimumAge) > Number(filters.maximumAge)
  ) {
    return "AGE_RANGE";
  }
  if (
    filters.minimumPrice !== "" &&
    filters.maximumPrice !== "" &&
    BigInt(marketplacePriceToMinor(filters.minimumPrice)) >
      BigInt(marketplacePriceToMinor(filters.maximumPrice))
  ) {
    return "PRICE_RANGE";
  }
  return null;
}

export function marketplacePriceToMinor(value: string): string {
  const match = PRICE_PATTERN.exec(value);
  if (match?.[1] === undefined) {
    throw new Error("Invalid Marketplace price.");
  }
  const fraction = match[2] ?? "";
  return (
    BigInt(match[1]) * 100n +
    BigInt(fraction.padEnd(2, "0") || "0")
  ).toString();
}

function boundedText(value: string | null, maximumLength: number): string {
  const normalized = value?.trim() ?? "";
  return normalized.length <= maximumLength ? normalized : "";
}

function validParam(value: string | null, pattern: RegExp): string {
  return value !== null && pattern.test(value) ? value : "";
}

function validOptional(value: string, pattern: RegExp): boolean {
  return value === "" || pattern.test(value);
}

function setNonEmpty(
  params: URLSearchParams,
  name: string,
  value: string,
): void {
  if (value !== "") params.set(name, value);
}
